/**
 * Expanded Question Bank: Domain 2 — Quantitative Reasoning (التفكير الكمي والاستدلال الرياضي)
 * 15 Stochastic Mathematical & Relational Templates with Scalable Vector Stimuli
 */

import { wrapSvg } from './svg-builder.js';

export const EXPANDED_QUANTITATIVE_TEMPLATES = [
  // 1. Prime Gap Progression
  {
    id: 'quant_prime_gap_progression',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const primes = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47, 53, 59, 61, 67, 71, 73, 79, 83, 89, 97];
      const step = difficulty === 2 ? 1 : 2;
      const startIdx = prng.int(1, 6);
      const seq = [];
      for (let i = 0; i < 5; i++) {
        seq.push(primes[startIdx + i * step]);
      }
      const correctVal = primes[startIdx + 5 * step];

      const distSet = new Set();
      distSet.add(correctVal + 2);
      distSet.add(correctVal - 2);
      distSet.add(correctVal + 4);
      distSet.add(correctVal + 6);
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusSvg = `<svg class="iq-svg-math-series" viewBox="0 0 380 75" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="380" height="75" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        <text x="190" y="46" fill="#38bdf8" font-family="system-ui, monospace" font-size="18" font-weight="700" text-anchor="middle">${seq.join('   •   ')}   •   ?</text>
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(val),
        text_en: String(val),
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو العدد الأولي التالي الذي يكمل المتتالية المنطقية: \u2066${seq.join(' , ')} , [ ? ]\u2069؟`,
        questionText_en: `What is the next prime number completing the sequence: ${seq.join(' , ')} , [ ? ]?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `المتتالية تتبع الأعداد الأولية المتتالية (أو بقفزات ثابتة). العدد الأولي التالي هو ${correctVal}.`,
        explanation_en: `The sequence follows consecutive prime numbers. The next prime is ${correctVal}.`
      };
    }
  },

  // 2. Interlocked Gear Train Ratios
  {
    id: 'quant_gear_rotations',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const teethA = prng.pick([12, 16, 20]);
      const teethB = 24;
      const teethC = prng.pick([8, 12]);
      const turnsA = prng.pick([2, 3, 4]);

      // Total teeth moved = turnsA * teethA
      // turnsC = (turnsA * teethA) / teethC
      const turnsC = (turnsA * teethA) / teethC;
      // Direction: A cw -> B ccw -> C cw
      const dir_ar = 'مع عقارب الساعة';
      const dir_en = 'Clockwise';

      const correctAns_ar = `${turnsC} دورات (${dir_ar})`;
      const correctAns_en = `${turnsC} rotations (${dir_en})`;

      const oppDir_ar = 'عكس عقارب الساعة';
      const oppDir_en = 'Counter-clockwise';

      const distractors = [
        { ar: `${turnsC} دورات (${oppDir_ar})`, en: `${turnsC} rotations (${oppDir_en})` },
        { ar: `${Math.round(turnsC * 1.5)} دورات (${dir_ar})`, en: `${Math.round(turnsC * 1.5)} rotations (${dir_en})` },
        { ar: `${Math.max(1, turnsC - 1)} دورات (${dir_ar})`, en: `${Math.max(1, turnsC - 1)} rotations (${dir_en})` }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusSvg = `<svg viewBox="0 0 380 140" xmlns="http://www.w3.org/2000/svg" class="iq-svg-gears">
        <rect width="380" height="140" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <!-- Gear A -->
        <g transform="translate(70, 70)">
          <circle r="36" fill="none" stroke="#38bdf8" stroke-width="3" stroke-dasharray="7 5"/>
          <circle r="12" fill="#38bdf8" opacity="0.3"/>
          <text y="5" fill="#f8fafc" font-size="12" font-weight="700" text-anchor="middle">A (${teethA}T)</text>
          <path d="M-22,-44 A44,44 0 0,1 22,-44" fill="none" stroke="#f59e0b" stroke-width="2.5" marker-end="url(#arrow)"/>
        </g>
        <!-- Gear B -->
        <g transform="translate(170, 70)">
          <circle r="46" fill="none" stroke="#94a3b8" stroke-width="3" stroke-dasharray="8 6"/>
          <circle r="14" fill="#94a3b8" opacity="0.3"/>
          <text y="5" fill="#f8fafc" font-size="12" font-weight="700" text-anchor="middle">B (${teethB}T)</text>
        </g>
        <!-- Gear C -->
        <g transform="translate(280, 70)">
          <circle r="30" fill="none" stroke="#10b981" stroke-width="3" stroke-dasharray="6 4"/>
          <circle r="10" fill="#10b981" opacity="0.3"/>
          <text y="5" fill="#f8fafc" font-size="12" font-weight="700" text-anchor="middle">C (${teethC}T)</text>
        </g>
      </svg>`;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en,
        value: c.ar
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا دار الترس (A) ذو ${teethA} سنًا بمقدار ${turnsA} دورات كاملة مع عقارب الساعة، فكم دورة يدور الترس (C) ذو ${teethC} سنًا وفي أي اتجاه؟`,
        questionText_en: `If Gear A (${teethA} teeth) rotates ${turnsA} full turns clockwise, how many rotations will Gear C (${teethC} teeth) complete and in which direction?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `عدد الأسنان المتحركة = ${turnsA} × ${teethA} = ${turnsA * teethA}. يدور الترس C بمقدار ${turnsA * teethA} ÷ ${teethC} = ${turnsC} دورات. والاتجاه: A (مع) ← B (عكس) ← C (مع).`,
        explanation_en: `Total teeth moved = ${turnsA} × ${teethA} = ${turnsA * teethA}. Gear C turns ${turnsA * teethA} ÷ ${teethC} = ${turnsC} rotations. Direction alternates: A (CW) -> B (CCW) -> C (CW).`
      };
    }
  },

  // 3. Two-Pan Balance Scale Logic
  {
    id: 'quant_balance_weights',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // 1 Cube = C spheres, 1 Cylinder = Y spheres
      // Eq 1: 1 Cylinder = 3 Spheres
      // Eq 2: 1 Cube + 1 Sphere = 1 Cylinder (so 1 Cube = 2 Spheres)
      // Question: How many Spheres balance 2 Cubes + 1 Cylinder?
      const cylinderSpheres = 3;
      const cubeSpheres = 2;
      const numCubes = prng.pick([2, 3]);
      const numCylinders = prng.pick([1, 2]);
      const correctVal = (numCubes * cubeSpheres) + (numCylinders * cylinderSpheres);

      const distSet = new Set();
      distSet.add(correctVal + 1);
      distSet.add(correctVal - 1);
      distSet.add(correctVal + 2);
      distSet.add(correctVal - 2);
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-subtitle">الميزان 1: [ أسطوانة واحدة ] = [ 3 كرات ]</div>
          <div class="iq-stimulus-card-subtitle">الميزان 2: [ مكعب واحد + كرة واحدة ] = [ أسطوانة واحدة ]</div>
          <div class="iq-stimulus-card-highlight">المطلوب: كم كرة تعادل [ ${numCubes} مكعبات + ${numCylinders === 1 ? 'أسطوانة واحدة' : 'أسطوانتان'} ]؟</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-subtitle">Scale 1: [ 1 Cylinder ] = [ 3 Spheres ]</div>
          <div class="iq-stimulus-card-subtitle">Scale 2: [ 1 Cube + 1 Sphere ] = [ 1 Cylinder ]</div>
          <div class="iq-stimulus-card-highlight">Target: How many spheres balance [ ${numCubes} Cubes + ${numCylinders === 1 ? '1 Cylinder' : `${numCylinders} Cylinders`} ]?</div>
        </div>
      `;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val} كرات`,
        text_en: `${val} spheres`,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بناءً على توازن الكتل في الموازين الموضحة، كم كرة تلزم لموازنة ${numCubes} مكعبات مع ${numCylinders === 1 ? 'أسطوانة واحدة' : 'أسطوانتين'}؟`,
        questionText_en: `Based on the balanced scales, how many spheres balance ${numCubes} cubes and ${numCylinders} cylinder(s)?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `من الميزان 1: الأسطوانة = 3 كرات. بالتعويض في الميزان 2: المكعب + 1 = 3، إذن المكعب = 2 كرات. إذن المجموع = (${numCubes} × 2) + (${numCylinders} × 3) = ${correctVal} كرات.`,
        explanation_en: `From Scale 1: 1 Cylinder = 3 Spheres. Substituting into Scale 2: 1 Cube + 1 = 3 -> 1 Cube = 2 Spheres. Total = (${numCubes} × 2) + (${numCylinders} × 3) = ${correctVal} spheres.`
      };
    }
  },

  // 4. Combined Work Rate Logic
  {
    id: 'quant_work_rate',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Worker A takes a hours, Worker B takes b hours
      // Pick pairs where (a*b)/(a+b) is clean integer or half
      const pairs = [
        { a: 6, b: 3, res: 2, label_ar: 'ساعتان', label_en: '2 hours' },
        { a: 12, b: 6, res: 4, label_ar: '4 ساعات', label_en: '4 hours' },
        { a: 10, b: 15, res: 6, label_ar: '6 ساعات', label_en: '6 hours' },
        { a: 20, b: 5, res: 4, label_ar: '4 ساعات', label_en: '4 hours' },
        { a: 12, b: 4, res: 3, label_ar: '3 ساعات', label_en: '3 hours' }
      ];
      const p = prng.pick(pairs);

      const distSet = new Set();
      distSet.add(p.res + 1);
      distSet.add(p.res + 2);
      distSet.add(Math.max(1, p.res - 1));
      distSet.add(Math.round((p.a + p.b) / 2));
      distSet.delete(p.res);
      const distractors = Array.from(distSet).slice(0, 3).map(v => ({
        ar: `${v} ساعات`,
        en: `${v} hours`,
        val: v
      }));

      const allChoices = prng.shuffle([
        { ar: p.label_ar, en: p.label_en, val: p.res, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-body">المضخة الأولى تملأ الخزان في: <span class="iq-stimulus-card-highlight">${p.a} ساعات</span></div>
          <div class="iq-stimulus-card-body">المضخة الثانية تملأ الخزان في: <span class="iq-stimulus-card-highlight">${p.b} ساعات</span></div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-body">Pump A fills the tank in: <span class="iq-stimulus-card-highlight">${p.a} hours</span></div>
          <div class="iq-stimulus-card-body">Pump B fills the tank in: <span class="iq-stimulus-card-highlight">${p.b} hours</span></div>
        </div>
      `;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en,
        value: c.val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا تم تشغيل المضختين معًا في نفس الوقت، كم ساعة تستغرقان لملء الخزان بالكامل؟`,
        questionText_en: `If both pumps run simultaneously, how many hours will it take to fill the tank completely?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `معدل العمل المشترك = (1/${p.a}) + (1/${p.b}) = (${p.a + p.b}) / ${p.a * p.b}. الوقت الإجمالي = ${p.a * p.b} ÷ ${p.a + p.b} = ${p.label_ar}.`,
        explanation_en: `Combined rate = 1/${p.a} + 1/${p.b}. Total time = (${p.a} × ${p.b}) / (${p.a} + ${p.b}) = ${p.label_en}.`
      };
    }
  },

  // 5. Venn Diagram Overlapping Sets
  {
    id: 'quant_venn_intersection',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const total = prng.pick([50, 60, 80, 100]);
      const both = prng.int(10, 20);
      const onlyA = prng.int(15, 25);
      const onlyB = prng.int(12, 22);
      const neither = total - (onlyA + onlyB + both);

      const totalA = onlyA + both;
      const totalB = onlyB + both;

      const correctVal = neither;
      const distSet = new Set();
      distSet.add(neither + 5);
      distSet.add(Math.max(2, neither - 5));
      distSet.add(neither + 10);
      distSet.add(total - (totalA + totalB));
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).filter(v => v >= 0).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml_ar = `<svg viewBox="0 0 380 120" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="120" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <circle cx="150" cy="65" r="42" fill="rgba(56, 189, 248, 0.2)" stroke="#38bdf8" stroke-width="2"/>
        <circle cx="230" cy="65" r="42" fill="rgba(236, 72, 153, 0.2)" stroke="#ec4899" stroke-width="2"/>
        <text x="130" y="70" fill="#f8fafc" font-size="13" font-weight="700">A</text>
        <text x="190" y="70" fill="#f8fafc" font-size="13" font-weight="700" text-anchor="middle">${both}</text>
        <text x="250" y="70" fill="#f8fafc" font-size="13" font-weight="700">B</text>
        <text x="190" y="24" fill="#94a3b8" font-size="12" text-anchor="middle">المجموع الكلي للمجموعة = ${total}</text>
      </svg>`;

      const stimulusHtml_en = `<svg viewBox="0 0 380 120" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="120" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <circle cx="150" cy="65" r="42" fill="rgba(56, 189, 248, 0.2)" stroke="#38bdf8" stroke-width="2"/>
        <circle cx="230" cy="65" r="42" fill="rgba(236, 72, 153, 0.2)" stroke="#ec4899" stroke-width="2"/>
        <text x="130" y="70" fill="#f8fafc" font-size="13" font-weight="700">A</text>
        <text x="190" y="70" fill="#f8fafc" font-size="13" font-weight="700" text-anchor="middle">${both}</text>
        <text x="250" y="70" fill="#f8fafc" font-size="13" font-weight="700">B</text>
        <text x="190" y="24" fill="#94a3b8" font-size="12" text-anchor="middle">Total Group Size = ${total}</text>
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val} أفراد`,
        text_en: `${val} members`,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في مجموعة تضم ${total} شخصاً، يتقن ${totalA} منهم اللغة الأولى (A)، و ${totalB} يتقنون اللغة الثانية (B)، بينما ${both} يتقنون اللغتين معاً. كم شخصاً لا يتقن أياً من اللغتين؟`,
        questionText_en: `In a group of ${total} people, ${totalA} speak language A, ${totalB} speak language B, and ${both} speak both. How many speak neither?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `مجموع من يتقن لغة واحدة على الأقل = ${totalA} + ${totalB} - ${both} = ${totalA + totalB - both}. إذن من لا يتقن أياً منهما = ${total} - ${totalA + totalB - both} = ${correctVal}.`,
        explanation_en: `Total speaking at least one = ${totalA} + ${totalB} - ${both} = ${totalA + totalB - both}. Speaking neither = ${total} - ${totalA + totalB - both} = ${correctVal}.`
      };
    }
  },

  // 6. Clock Hands Angle
  {
    id: 'quant_clock_hands_angle',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Pick clean times
      const times = [
        { h: 3, m: 30, angle: 75 },
        { h: 8, m: 20, angle: 130 },
        { h: 2, m: 20, angle: 50 },
        { h: 4, m: 40, angle: 100 },
        { h: 9, m: 30, angle: 105 }
      ];
      const t = prng.pick(times);
      const correctVal = t.angle;

      const distSet = new Set();
      distSet.add(correctVal + 15);
      distSet.add(Math.max(10, correctVal - 15));
      distSet.add(correctVal + 30);
      distSet.add(Math.max(10, correctVal - 30));
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const hourAngle = (t.h % 12) * 30 + t.m * 0.5;
      const minAngle = t.m * 6;

      const stimulusSvg = `<svg viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg" class="iq-svg-clock" style="max-height:140px; margin:0 auto; display:block;">
        <circle cx="80" cy="80" r="72" fill="rgba(14, 22, 40, 0.7)" stroke="#38bdf8" stroke-width="3"/>
        <!-- Hour hand -->
        <line x1="80" y1="80" x2="${80 + 38 * Math.sin(hourAngle * Math.PI / 180)}" y2="${80 - 38 * Math.cos(hourAngle * Math.PI / 180)}" stroke="#f8fafc" stroke-width="4" stroke-linecap="round"/>
        <!-- Minute hand -->
        <line x1="80" y1="80" x2="${80 + 55 * Math.sin(minAngle * Math.PI / 180)}" y2="${80 - 55 * Math.cos(minAngle * Math.PI / 180)}" stroke="#38bdf8" stroke-width="2.5" stroke-linecap="round"/>
        <circle cx="80" cy="80" r="5" fill="#f8fafc"/>
        <text x="80" y="24" fill="#94a3b8" font-size="11" font-weight="700" text-anchor="middle">12</text>
        <text x="140" y="84" fill="#94a3b8" font-size="11" font-weight="700" text-anchor="middle">3</text>
        <text x="80" y="144" fill="#94a3b8" font-size="11" font-weight="700" text-anchor="middle">6</text>
        <text x="20" y="84" fill="#94a3b8" font-size="11" font-weight="700" text-anchor="middle">9</text>
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val}°`,
        text_en: `${val}°`,
        value: val
      }));

      const timeStr = `${t.h}:${t.m < 10 ? '0' + t.m : t.m}`;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هي الزاوية الصغرى بالدرجات بين عقرب الساعات وعقرب الدقائق عند تمام الساعة ${timeStr}؟`,
        questionText_en: `What is the smaller angle in degrees between the hour and minute hands at ${timeStr}?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `زاوية عقرب الساعات = (${t.h} × 30) + (${t.m} × 0.5) = ${hourAngle}°، وزاوية الدقائق = ${t.m} × 6 = ${minAngle}°. الفرق بينهما = |${hourAngle} - ${minAngle}| = ${correctVal}°.`,
        explanation_en: `Hour hand angle = (${t.h} × 30) + (${t.m} × 0.5) = ${hourAngle}°, Minute hand angle = ${t.m} × 6 = ${minAngle}°. Difference = ${correctVal}°.`
      };
    }
  },

  // 7. Chained Percentage Price Changes
  {
    id: 'quant_compound_percentage',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const pct = prng.pick([10, 20, 25, 30]);
      // Price increases by pct%, then decreases by pct%
      // Final = 1 * (1 + p/100) * (1 - p/100) = 1 - (p/100)^2
      const lossPct = ((pct * pct) / 100);

      const correctAns_ar = `انخفاض بنسبة ${lossPct}%`;
      const correctAns_en = `Decreased by ${lossPct}%`;

      const distractors = [
        { ar: 'لا يتغير السعر إطلاقاً (0%)', en: 'No change (0%)' },
        { ar: `ارتفاع بنسبة ${lossPct}%`, en: `Increased by ${lossPct}%` },
        { ar: `انخفاض بنسبة ${pct}%`, en: `Decreased by ${pct}%` }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">السعر الأصلي: [ 100% ]</div>
          <div class="iq-stimulus-card-body">ارتفاع أولاً بـ <span class="iq-stimulus-card-highlight">+${pct}%</span>  ←  ثم انخفاض بـ <span class="iq-stimulus-card-highlight">-${pct}%</span></div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Original Value: [ 100% ]</div>
          <div class="iq-stimulus-card-body">First increase by <span class="iq-stimulus-card-highlight">+${pct}%</span>  ←  Then decrease by <span class="iq-stimulus-card-highlight">-${pct}%</span></div>
        </div>
      `;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en,
        value: c.ar
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا ارتفع سعر سلعة بنسبة ${pct}%، ثم تم تخفيض السعر الجديد بنسبة ${pct}%، فما هي النتيجة الصافية مقارنة بالسعر الأصلي؟`,
        questionText_en: `If a price increases by ${pct}%, and the new price is then discounted by ${pct}%, what is the net effect compared to the original price?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `النسبة النهائية = (1 + ${pct/100}) × (1 - ${pct/100}) = 1 - ${(pct*pct)/10000} = ${100 - lossPct}%. أي انخفاض دائم بمقدار مربع النسبة مقسوماً على 100 (${lossPct}%).`,
        explanation_en: `Final factor = (1 + ${pct/100}) × (1 - ${pct/100}) = 1 - ${(pct*pct)/10000} = ${100 - lossPct}%, resulting in a net decrease of ${lossPct}%.`
      };
    }
  },

  // 8. Grid Coordinate Pythagorean Distance
  {
    id: 'quant_pythagorean_distance',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Triples: 3-4-5, 6-8-10, 5-12-13, 8-15-17
      const triples = [
        { north: 6, east: 8, dist: 10 },
        { north: 9, east: 12, dist: 15 },
        { north: 5, east: 12, dist: 13 },
        { north: 8, east: 15, dist: 17 }
      ];
      const t = prng.pick(triples);
      const correctVal = t.dist;

      const distSet = new Set();
      distSet.add(t.north + t.east);
      distSet.add(t.dist + 2);
      distSet.add(Math.max(1, t.dist - 2));
      distSet.add(t.north + t.east - 3);
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusSvg = `<svg viewBox="0 0 380 90" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="90" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <path d="M70,70 L70,25 L160,25" fill="none" stroke="#38bdf8" stroke-width="2.5" stroke-dasharray="4 3"/>
        <line x1="70" y1="70" x2="160" y2="25" stroke="#f59e0b" stroke-width="2.5"/>
        <text x="50" y="48" fill="#94a3b8" font-size="12" text-anchor="middle">${t.north}m</text>
        <text x="115" y="18" fill="#94a3b8" font-size="12" text-anchor="middle">${t.east}m</text>
        <text x="125" y="60" fill="#f59e0b" font-size="12" font-weight="700">المسافة المستقيمة ؟</text>
        <text x="300" y="50" fill="#f8fafc" font-size="13" text-anchor="middle">تحرك: ${t.north}م شمالاً ثم ${t.east}م شرقاً</text>
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val} أمتار`,
        text_en: `${val} meters`,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `انطلق قارب مسافة ${t.north} أمتار باتجاه الشمال، ثم انعطف بزاوية قائمة واتجه ${t.east} أمتار شرقاً. ما هي أقصر مسافة مستقيمة تفصله عن نقطة البداية؟`,
        questionText_en: `A boat travels ${t.north} meters North, turns 90 degrees and travels ${t.east} meters East. What is the shortest straight-line distance back to the starting point?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `باستخدام نظرية فيثاغورس: المسافة = جذر (${t.north}² + ${t.east}²) = جذر (${t.north * t.north} + ${t.east * t.east}) = ${correctVal} متراً.`,
        explanation_en: `Using the Pythagorean theorem: Distance = sqrt(${t.north}² + ${t.east}²) = sqrt(${t.north * t.north + t.east * t.east}) = ${correctVal} meters.`
      };
    }
  },

  // 9. Relative Speed Intercept
  {
    id: 'quant_speed_intercept',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const v1 = prng.pick([60, 70, 80]);
      const v2 = prng.pick([40, 50, 60]);
      const hours = prng.pick([2, 3, 4]);
      const totalDistance = (v1 + v2) * hours;

      const correctVal = hours;
      const distSet = new Set();
      distSet.add(hours + 1);
      distSet.add(Math.max(1, hours - 1));
      distSet.add(hours + 2);
      distSet.add(Math.round(totalDistance / v1));
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusSvg = `<svg viewBox="0 0 380 80" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="80" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <line x1="40" y1="40" x2="340" y2="40" stroke="rgba(255,255,255,0.2)" stroke-width="2"/>
        <circle cx="40" cy="40" r="8" fill="#38bdf8"/>
        <circle cx="340" cy="40" r="8" fill="#ec4899"/>
        <text x="40" y="62" fill="#38bdf8" font-size="11" font-weight="700" text-anchor="middle">${v1} كم/س →</text>
        <text x="340" y="62" fill="#ec4899" font-size="11" font-weight="700" text-anchor="middle">← ${v2} كم/س</text>
        <text x="190" y="28" fill="#f8fafc" font-size="13" font-weight="700" text-anchor="middle">المسافة بينهما: ${totalDistance} كم</text>
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val} ساعات`,
        text_en: `${val} hours`,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `انطلقت سيارتان من نقطتين المسافة بينهما ${totalDistance} كم متوجهتين نحو بعضهما؛ الأولى بسرعة ${v1} كم/س والثانية بسرعة ${v2} كم/س. بعد كم ساعة تلتقيان؟`,
        questionText_en: `Two cars start ${totalDistance} km apart driving toward each other; one at ${v1} km/h and the other at ${v2} km/h. After how many hours will they meet?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `السرعة النسبية = ${v1} + ${v2} = ${v1 + v2} كم/س. زمن اللقاء = المسافة ÷ السرعة النسبية = ${totalDistance} ÷ ${v1 + v2} = ${correctVal} ساعات.`,
        explanation_en: `Relative closing speed = ${v1} + ${v2} = ${v1 + v2} km/h. Meeting time = ${totalDistance} / ${v1 + v2} = ${correctVal} hours.`
      };
    }
  },

  // 10. Modular Arithmetic Day of Week
  {
    id: 'quant_modular_calendar',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const days_ar = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
      const days_en = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

      const startDayIdx = prng.int(0, 6);
      const daysAdded = prng.pick([30, 45, 60, 75, 100]);
      const targetDayIdx = (startDayIdx + daysAdded) % 7;

      const correctAns_ar = days_ar[targetDayIdx];
      const correctAns_en = days_en[targetDayIdx];

      const otherIndices = [0, 1, 2, 3, 4, 5, 6].filter(i => i !== targetDayIdx);
      const distIndices = prng.sample(otherIndices, 3);

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distIndices.map(i => ({ ar: days_ar[i], en: days_en[i], isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-body">اليوم الحالي هو يوم: <span class="iq-stimulus-card-highlight">${days_ar[startDayIdx]}</span></div>
          <div class="iq-stimulus-card-title">ما هو اليوم بعد مرور [ ${daysAdded} يوماً ] بالتمام؟</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-body">Current day of week: <span class="iq-stimulus-card-highlight">${days_en[startDayIdx]}</span></div>
          <div class="iq-stimulus-card-title">What day will it be after exactly [ ${daysAdded} days ]?</div>
        </div>
      `;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en,
        value: c.ar
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا كان اليوم هو يوم ${days_ar[startDayIdx]}، فما هو اليوم الذي سيوافق التاريخ بعد مرور ${daysAdded} يوماً من اليوم؟`,
        questionText_en: `If today is ${days_en[startDayIdx]}, what day of the week will it be exactly ${daysAdded} days from now?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `الأسابيع تتكرر كل 7 أيام. باقي قسمة ${daysAdded} على 7 هو ${daysAdded % 7}. بإضافة ${daysAdded % 7} أيام إلى ${days_ar[startDayIdx]}، نحصل على يوم ${correctAns_ar}.`,
        explanation_en: `Days repeat mod 7. ${daysAdded} mod 7 = ${daysAdded % 7}. Adding ${daysAdded % 7} days to ${days_en[startDayIdx]} lands on ${correctAns_en}.`
      };
    }
  },

  // 11. 3x3 Numeric Magic Square Relation
  {
    id: 'quant_matrix_row_ops',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Row 3 = Row 1 * 2 + Row 2 (or Row 1 + Row 2 * 2)
      const r1 = [prng.int(2, 6), prng.int(3, 7), prng.int(1, 5)];
      const r2 = [prng.int(1, 4), prng.int(2, 5), prng.int(3, 6)];
      const r3 = [
        r1[0] * 2 + r2[0],
        r1[1] * 2 + r2[1],
        r1[2] * 2 + r2[2]
      ];

      const correctVal = r3[2];
      const distSet = new Set();
      distSet.add(correctVal + 2);
      distSet.add(correctVal - 2);
      distSet.add(correctVal + 4);
      distSet.add(r1[2] + r2[2]);
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusSvg = `<svg viewBox="0 0 240 180" xmlns="http://www.w3.org/2000/svg" style="max-height:160px; margin:0 auto; display:block;">
        <rect width="240" height="180" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <!-- 3x3 grid cells -->
        <rect x="20" y="20" width="60" height="40" rx="6" fill="rgba(255,255,255,0.05)"/>
        <text x="50" y="46" fill="#f8fafc" font-size="16" font-weight="700" text-anchor="middle">${r1[0]}</text>
        <rect x="90" y="20" width="60" height="40" rx="6" fill="rgba(255,255,255,0.05)"/>
        <text x="120" y="46" fill="#f8fafc" font-size="16" font-weight="700" text-anchor="middle">${r1[1]}</text>
        <rect x="160" y="20" width="60" height="40" rx="6" fill="rgba(255,255,255,0.05)"/>
        <text x="190" y="46" fill="#f8fafc" font-size="16" font-weight="700" text-anchor="middle">${r1[2]}</text>

        <rect x="20" y="70" width="60" height="40" rx="6" fill="rgba(255,255,255,0.05)"/>
        <text x="50" y="96" fill="#f8fafc" font-size="16" font-weight="700" text-anchor="middle">${r2[0]}</text>
        <rect x="90" y="70" width="60" height="40" rx="6" fill="rgba(255,255,255,0.05)"/>
        <text x="120" y="96" fill="#f8fafc" font-size="16" font-weight="700" text-anchor="middle">${r2[1]}</text>
        <rect x="160" y="70" width="60" height="40" rx="6" fill="rgba(255,255,255,0.05)"/>
        <text x="190" y="96" fill="#f8fafc" font-size="16" font-weight="700" text-anchor="middle">${r2[2]}</text>

        <rect x="20" y="120" width="60" height="40" rx="6" fill="rgba(255,255,255,0.05)"/>
        <text x="50" y="146" fill="#38bdf8" font-size="16" font-weight="700" text-anchor="middle">${r3[0]}</text>
        <rect x="90" y="120" width="60" height="40" rx="6" fill="rgba(255,255,255,0.05)"/>
        <text x="120" y="146" fill="#38bdf8" font-size="16" font-weight="700" text-anchor="middle">${r3[1]}</text>
        <rect x="160" y="120" width="60" height="40" rx="6" fill="rgba(56, 189, 248, 0.2)" stroke="#38bdf8" stroke-width="2"/>
        <text x="190" y="146" fill="#38bdf8" font-size="18" font-weight="700" text-anchor="middle">?</text>
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(val),
        text_en: String(val),
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تخضع صفوف المصفوفة العددية لقاعدة رياضية موحدة بين عناصرها. ما هو الرقم الناقص في الخلية الأخيرة؟`,
        questionText_en: `The rows of the numerical matrix adhere to a uniform mathematical operation. Which number completes the bottom-right cell?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `القاعدة: عناصر الصف الثالث = (عناصر الصف الأول × 2) + عناصر الصف الثاني. إذن: (${r1[2]} × 2) + ${r2[2]} = ${correctVal}.`,
        explanation_en: `Rule: Row 3 = (Row 1 × 2) + Row 2. Thus: (${r1[2]} × 2) + ${r2[2]} = ${correctVal}.`
      };
    }
  },

  // 12. Independent Probability Balls in Bag
  {
    id: 'quant_probability_draw',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const red = prng.pick([3, 4, 5]);
      const blue = prng.pick([3, 4, 5]);
      const total = red + blue;
      // Prob of drawing 2 red balls without replacement = (red/total) * ((red-1)/(total-1))
      const num = red * (red - 1);
      const den = total * (total - 1);

      // Simplify fraction
      const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
      const g = gcd(num, den);
      const sNum = num / g;
      const sDen = den / g;

      const correctAns = `${sNum}/${sDen}`;
      const distSet = new Set();
      distSet.add(`${sNum}/${sDen + 2}`);
      distSet.add(`${Math.min(sDen - 1, sNum + 1)}/${sDen}`);
      distSet.add(`${red}/${total}`);
      distSet.add(`${sNum + 1}/${sDen + 3}`);
      distSet.delete(correctAns);
      const distractors = Array.from(distSet).slice(0, 3);

      const allChoices = prng.shuffle([correctAns, ...distractors]);
      const correctIndex = allChoices.indexOf(correctAns);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-body">كيس يحتوي على: [ ${red} كرات حمراء ] و [ ${blue} كرات زرقاء ]</div>
          <div class="iq-stimulus-card-title">سحب كرتين عشوائياً على التوالي دون إرجاع</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-body">Urn contains: [ ${red} red balls ] and [ ${blue} blue balls ]</div>
          <div class="iq-stimulus-card-title">Two balls drawn sequentially without replacement</div>
        </div>
      `;

      const options = allChoices.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: val,
        text_en: val,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا سُحبت كرتان عشوائياً واحدة تلو الأخرى دون إرجاع، فما هو احتمال أن تكون الكرتان المسحوبتان حمراوين معاً؟`,
        questionText_en: `If two balls are drawn at random without replacement, what is the probability that both balls are red?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `احتمال الكرة الأولى حمراء = ${red}/${total}، واحتمال الثانية حمراء = ${red - 1}/${total - 1}. الاحتمال المشترك = (${red}/${total}) × (${red - 1}/${total - 1}) = ${correctAns}.`,
        explanation_en: `First ball red = ${red}/${total}, second ball red = ${red - 1}/${total - 1}. Combined probability = (${red}/${total}) × (${red - 1}/${total - 1}) = ${correctAns}.`
      };
    }
  },

  // 13. Geometric Ratio Folding Scale
  {
    id: 'quant_paper_folding_layers',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 30,
    generate(prng, difficulty = 1) {
      const folds = prng.pick([5, 6, 7]);
      const correctVal = Math.pow(2, folds);

      const distSet = new Set();
      distSet.add(folds * 2);
      distSet.add(Math.pow(2, folds - 1));
      distSet.add(Math.pow(2, folds) + 16);
      distSet.add(Math.pow(2, folds + 1));
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-muted">طي ورقة من المنتصف بشكل متكرر: كل طية تضاعف عدد الطبقات</div>
          <div class="iq-stimulus-card-title">تم طي الورقة تماماً [ ${folds} مرات متتالية ]</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-muted">Repeated half-folding: each fold doubles the layer count</div>
          <div class="iq-stimulus-card-title">Paper folded consecutively [ ${folds} times ]</div>
        </div>
      `;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val} طبقة`,
        text_en: `${val} layers`,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا تم طي ورقة مسطحة من المنتصف ${folds} مرات متتالية، فكم طبقة ورقية ستتكون في النهاية؟`,
        questionText_en: `If a flat sheet of paper is folded in half ${folds} consecutive times, how many layers will it have?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `كل طية تضاعف عدد الطبقات بصيغة أُسّية: 2^${folds} = ${correctVal} طبقة.`,
        explanation_en: `Each fold doubles the number of layers exponentially: 2^${folds} = ${correctVal} layers.`
      };
    }
  },

  // 14. Average Adjustment (Added Member)
  {
    id: 'quant_average_update',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const count = prng.pick([4, 5, 6]);
      const oldAvg = prng.pick([20, 25, 30]);
      const newScore = prng.pick([oldAvg + 10, oldAvg + 15, oldAvg + 20]);
      const newAvg = ((count * oldAvg) + newScore) / (count + 1);

      const correctVal = newAvg;
      const distSet = new Set();
      distSet.add(newAvg + 1);
      distSet.add(newAvg - 1);
      distSet.add(oldAvg + 2);
      distSet.add(Math.round((oldAvg + newScore) / 2));
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-body">متوسط درجات ${count} طلاب = <span class="iq-stimulus-card-highlight">${oldAvg}</span></div>
          <div class="iq-stimulus-card-title">انضم طالب جديد نال درجة: [ ${newScore} ]</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-body">Average score of ${count} students = <span class="iq-stimulus-card-highlight">${oldAvg}</span></div>
          <div class="iq-stimulus-card-title">An additional student joined scoring: [ ${newScore} ]</div>
        </div>
      `;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(val),
        text_en: String(val),
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا كان متوسط درجات ${count} طلاب هو ${oldAvg}، ثم انضم طالب جديد حاصلاً على ${newScore}، فما هو المتوسط الجديد لكافة الطلاب؟`,
        questionText_en: `If the average test score of ${count} students is ${oldAvg}, and an additional student scores ${newScore}, what is the new group average?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `المجموع القديم = ${count} × ${oldAvg} = ${count * oldAvg}. المجموع الجديد = ${count * oldAvg} + ${newScore} = ${(count * oldAvg) + newScore}. المتوسط الجديد = ${(count * oldAvg) + newScore} ÷ ${count + 1} = ${correctVal}.`,
        explanation_en: `Initial sum = ${count} × ${oldAvg} = ${count * oldAvg}. New sum = ${count * oldAvg} + ${newScore} = ${(count * oldAvg) + newScore}. New average = ${(count * oldAvg) + newScore} / ${count + 1} = ${correctVal}.`
      };
    }
  },

  // 15. Alternating Geometric-Arithmetic Hybrid Sequence
  {
    id: 'quant_hybrid_alternating_seq',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Rule: * 2, - 3, * 2, - 3...
      const start = prng.int(3, 7);
      const mult = 2;
      const sub = prng.int(2, 4);

      const seq = [start];
      let curr = start;
      for (let i = 0; i < 5; i++) {
        if (i % 2 === 0) {
          curr = curr * mult;
        } else {
          curr = curr - sub;
        }
        seq.push(curr);
      }

      // Next is index 5 (which is even => curr * mult or curr - sub)
      const isNextMult = (seq.length - 1) % 2 === 0;
      const correctVal = isNextMult ? (curr * mult) : (curr - sub);

      const distSet = new Set();
      distSet.add(isNextMult ? (curr - sub) : (curr * mult));
      distSet.add(correctVal + sub);
      distSet.add(correctVal - sub);
      distSet.add(correctVal + 2);
      distSet.delete(correctVal);
      const distractors = Array.from(distSet).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusSvg = `<svg class="iq-svg-math-series" viewBox="0 0 380 75" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="75" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.12)"/>
        <text x="190" y="46" fill="#38bdf8" font-family="system-ui, monospace" font-size="18" font-weight="700" text-anchor="middle">${seq.join('   •   ')}   •   ?</text>
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(val),
        text_en: String(val),
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الرقم التالي الذي يكمل المتتابعة المركبة: \u2066${seq.join(' , ')} , [ ? ]\u2069؟`,
        questionText_en: `What is the next number completing the alternating sequence: ${seq.join(' , ')} , [ ? ]?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `تتناوب العمليات الحسابية بالضرب في ${mult} ثم طرح ${sub}. الرقم التالي هو ${correctVal}.`,
        explanation_en: `Operations alternate between multiplying by ${mult} and subtracting ${sub}. The next value is ${correctVal}.`
      };
    }
  }
];
